// frontend/src/components/profile/CreateTicketModal.js
import React, { useState, useEffect } from 'react';
import { supportApi } from '../../api/support';
import { ordersApi } from '../../api/orders';
import { servicesApi } from '../../api/services';
import uploadService from '../../services/upload';
import { compressImage } from '../../utils/imageCompression';
import { FaTimes, FaPaperclip, FaSpinner, FaCheck } from 'react-icons/fa';

function CreateTicketModal({ onClose, onTicketCreated }) {
  const [loading, setLoading] = useState(false);
  const [orders, setOrders] = useState([]);
  const [bookings, setBookings] = useState([]);
  
  // Form State
  const [subject, setSubject] = useState('');
  const [category, setCategory] = useState('Order Issue');
  const [priority, setPriority] = useState('medium');
  const [description, setDescription] = useState('');
  const [relatedOrderId, setRelatedOrderId] = useState('');
  const [relatedBookingId, setRelatedBookingId] = useState('');
  const [attachments, setAttachments] = useState([]);
  const [uploading, setUploading] = useState(false);

  // Fetch Context Data (Orders/Bookings)
  useEffect(() => {
    const fetchContext = async () => {
      try {
        const [ordersData, bookingsData] = await Promise.all([
          ordersApi.getUserOrders(),
          servicesApi.getBookingHistory()
        ]);
        setOrders(ordersData);
        setBookings(bookingsData);
      } catch (err) {
        console.error("Failed to load context data", err);
      }
    };
    fetchContext();
  }, []);

  const handleImageUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;

    setUploading(true);
    try {
      const uploadPromises = files.map(async (file) => {
        const compressed = await compressImage(file, 0.6);
        const res = await uploadService.uploadImage(compressed, 'support-attachments');
        return res.url;
      });
      
      const urls = await Promise.all(uploadPromises);
      setAttachments(prev => [...prev, ...urls]);
    } catch (err) {
      alert("Failed to upload images.");
    } finally {
      setUploading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    const payload = {
      subject,
      category,
      priority,
      description,
      attachments,
      related_order_id: relatedOrderId || null,
      related_booking_id: relatedBookingId || null
    };

    try {
      await supportApi.createTicket(payload);
      onTicketCreated();
      onClose();
    } catch (err) {
      alert("Failed to create ticket. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] flex flex-col">
        
        <div className="flex justify-between items-center p-6 border-b">
          <h3 className="text-xl font-bold text-gray-800">Create Support Ticket</h3>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-700"><FaTimes /></button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 overflow-y-auto space-y-4">
          
          {/* Subject & Priority */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="md:col-span-2">
              <label className="block text-sm font-bold text-gray-700 mb-1">Subject</label>
              <input 
                required 
                value={subject} 
                onChange={e => setSubject(e.target.value)} 
                className="w-full p-2 border rounded focus:ring-2 focus:ring-primary focus:outline-none"
                placeholder="Brief summary of the issue"
              />
            </div>
            <div>
              <label className="block text-sm font-bold text-gray-700 mb-1">Priority</label>
              <select 
                value={priority} 
                onChange={e => setPriority(e.target.value)} 
                className="w-full p-2 border rounded bg-white"
              >
                <option value="low">Low</option>
                <option value="medium">Medium</option>
                <option value="high">High</option>
              </select>
            </div>
          </div>

          {/* Category & Context Linking */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-bold text-gray-700 mb-1">Category</label>
              <select 
                value={category} 
                onChange={e => {
                  setCategory(e.target.value);
                  setRelatedOrderId('');
                  setRelatedBookingId('');
                }} 
                className="w-full p-2 border rounded bg-white"
              >
                <option value="Order Issue">Order Issue</option>
                <option value="Service Booking">Service Booking</option>
                <option value="Payment">Payment</option>
                <option value="Technical">Technical Issue</option>
                <option value="General">General Inquiry</option>
              </select>
            </div>

            {/* Dynamic Dropdown based on Category */}
            {category === 'Order Issue' && (
              <div>
                <label className="block text-sm font-bold text-gray-700 mb-1">Related Order (Optional)</label>
                <select 
                  value={relatedOrderId} 
                  onChange={e => setRelatedOrderId(e.target.value)} 
                  className="w-full p-2 border rounded bg-white"
                >
                  <option value="">-- Select Order --</option>
                  {orders.map(o => (
                    <option key={o.id} value={o.id}>
                      {o.order_number} ({new Date(o.created_at).toLocaleDateString()})
                    </option>
                  ))}
                </select>
              </div>
            )}

            {category === 'Service Booking' && (
              <div>
                <label className="block text-sm font-bold text-gray-700 mb-1">Related Booking (Optional)</label>
                <select 
                  value={relatedBookingId} 
                  onChange={e => setRelatedBookingId(e.target.value)} 
                  className="w-full p-2 border rounded bg-white"
                >
                  <option value="">-- Select Booking --</option>
                  {bookings.map(b => (
                    <option key={b.id} value={b.id}>
                      {b.booking_reference} - {b.service_type}
                    </option>
                  ))}
                </select>
              </div>
            )}
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-bold text-gray-700 mb-1">Description</label>
            <textarea 
              required 
              rows="5"
              value={description} 
              onChange={e => setDescription(e.target.value)} 
              className="w-full p-2 border rounded focus:ring-2 focus:ring-primary focus:outline-none"
              placeholder="Please describe your issue in detail..."
            ></textarea>
          </div>

          {/* Attachments */}
          <div>
            <label className="block text-sm font-bold text-gray-700 mb-2">Attachments (Images)</label>
            <div className="flex items-center gap-4">
              <label className="cursor-pointer bg-gray-100 hover:bg-gray-200 text-gray-600 px-4 py-2 rounded border border-gray-300 flex items-center gap-2 transition">
                <FaPaperclip /> Select Images
                <input type="file" multiple accept="image/*" onChange={handleImageUpload} className="hidden" disabled={uploading} />
              </label>
              {uploading && <span className="text-sm text-blue-600 flex items-center gap-1"><FaSpinner className="animate-spin" /> Uploading...</span>}
            </div>
            
            {/* Preview */}
            {attachments.length > 0 && (
              <div className="flex gap-2 mt-3 overflow-x-auto pb-2">
                {attachments.map((url, idx) => (
                  <div key={idx} className="relative w-16 h-16 border rounded overflow-hidden flex-shrink-0">
                    <img src={url} alt="attachment" className="w-full h-full object-cover" />
                    <button 
                      type="button"
                      onClick={() => setAttachments(prev => prev.filter((_, i) => i !== idx))}
                      className="absolute top-0 right-0 bg-red-500 text-white p-0.5 rounded-bl text-xs"
                    >
                      <FaTimes />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="flex justify-end gap-3 pt-4 border-t">
            <button type="button" onClick={onClose} className="px-4 py-2 text-gray-600">Cancel</button>
            <button 
              type="submit" 
              disabled={loading || uploading} 
              className="px-6 py-2 bg-primary text-white font-bold rounded hover:bg-primary-dark flex items-center gap-2"
            >
              {loading ? <FaSpinner className="animate-spin" /> : <FaCheck />} Submit Ticket
            </button>
          </div>

        </form>
      </div>
    </div>
  );
}

export default CreateTicketModal;